import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Layout";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plane, CheckCircle2, XCircle } from "lucide-react";
import ProjectIdSettings from "@/components/app/ProjectIdSettings";

const statusTone = (s) => s === "Approved" ? "bg-[#E9F5EE] text-[#175E33]"
  : s === "Rejected" ? "bg-[#FFE7DE] text-[#7A1A00]"
  : "bg-gold/20 text-royal-800";

export default function Settings() {
  const { user, refresh } = useAuth();
  const isSuperAdmin = user.role === "SuperAdmin";
  const isSaOrTL = ["SuperAdmin", "TeamLeader"].includes(user.role);
  const canRequestLeave = ["Manager", "TeamLeader"].includes(user.role);
  const [org, setOrg] = useState(null);
  const [profile, setProfile] = useState({ name: user.name, phone: "", upi: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [leave, setLeave] = useState({ start_date: "", end_date: "", reason: "" });
  const [leaves, setLeaves] = useState([]);
  const [leaveBusy, setLeaveBusy] = useState(false);

  useEffect(() => {
    if (isSuperAdmin) api.get("/settings/org").then((r) => setOrg(r.data)).catch(() => {});
    if (user.linked_freelancer_id) api.get(`/freelancers/${user.linked_freelancer_id}`).then((r) => {
      setProfile({ name: r.data.name, phone: r.data.phone || "", upi: r.data.payment_details?.upi || "" });
    }).catch(() => {});
    if (canRequestLeave) loadLeaves();
  }, [isSuperAdmin, user.linked_freelancer_id, canRequestLeave]);

  const loadLeaves = async () => {
    try { const { data } = await api.get("/leave-requests"); setLeaves(data); } catch {}
  };

  const saveOrg = async (e) => {
    e.preventDefault();
    try { await api.put("/settings/org", org); toast.success("Saved"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/freelancers/${user.linked_freelancer_id}`, {
        name: profile.name, phone: profile.phone, payment_details: { upi: profile.upi },
      });
      toast.success("Profile updated"); refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/change-password", pw); setPw({ current_password: "", new_password: "" }); toast.success("Password changed"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const submitLeave = async (e) => {
    e.preventDefault();
    if (!leave.start_date || !leave.end_date || !leave.reason.trim()) {
      toast.error("Please fill start date, end date, and reason"); return;
    }
    if (leave.end_date < leave.start_date) {
      toast.error("End date must be on or after start date"); return;
    }
    setLeaveBusy(true);
    try {
      await api.post("/leave-requests", leave);
      setLeave({ start_date: "", end_date: "", reason: "" });
      toast.success("Leave request submitted — your Team Leader or Super Admin has been notified.");
      loadLeaves();
    } catch (err) { toast.error(formatApiError(err)); }
    setLeaveBusy(false);
  };

  return (
    <div>
      <PageHeader title="Settings" description={isSuperAdmin ? "Organisation, notifications and your account." : "Your profile, leave requests, and password."} />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {isSuperAdmin && org && (
          <form onSubmit={saveOrg} className="border border-neutral-200 bg-white p-6 space-y-3 rounded-2xl" data-testid="org-settings">
            <div className="label-caps">Organisation</div>
            <div><label className="label-caps block mb-1">Studio Name</label><Input value={org.name || ""} onChange={(e) => setOrg({ ...org, name: e.target.value })} className="rounded-lg border-black/15" /></div>
            <div><label className="label-caps block mb-1">Contact Email</label><Input type="email" value={org.email || ""} onChange={(e) => setOrg({ ...org, email: e.target.value })} className="rounded-lg border-black/15" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-caps block mb-1">Currency</label><Input value={org.currency || "INR"} onChange={(e) => setOrg({ ...org, currency: e.target.value })} className="rounded-lg border-black/15" disabled /></div>
              <div><label className="label-caps block mb-1">Timezone</label><Input value={org.timezone || "Asia/Kolkata"} onChange={(e) => setOrg({ ...org, timezone: e.target.value })} className="rounded-lg border-black/15" /></div>
            </div>
            <button className="btn-primary">Save Organisation</button>
          </form>
        )}
        {isSaOrTL && <ProjectIdSettings />}
        {user.linked_freelancer_id && (
          <form onSubmit={saveProfile} className="border border-neutral-200 bg-white p-6 space-y-3 rounded-2xl" data-testid="profile-settings">
            <div className="label-caps">Profile</div>
            <div><label className="label-caps block mb-1">Name</label><Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="rounded-lg border-black/15" /></div>
            <div><label className="label-caps block mb-1">Phone</label><Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="rounded-lg border-black/15" /></div>
            <div><label className="label-caps block mb-1">UPI / Payment ID</label><Input value={profile.upi} onChange={(e) => setProfile({ ...profile, upi: e.target.value })} className="rounded-lg border-black/15" /></div>
            <button className="btn-primary">Save Profile</button>
          </form>
        )}

        {canRequestLeave && (
          <div className="lg:col-span-2 border border-black/10 bg-white p-6 rounded-2xl" data-testid="leave-request-panel">
            <div className="flex items-center gap-2 label-caps text-royal-800"><Plane size={12} /> Request Leave</div>
            <p className="text-sm text-ink/60 mt-1">Submit a leave request — your Team Leader or Super Admin can approve, and any assigned projects will auto-reassign to their per-project backup manager.</p>
            <form onSubmit={submitLeave} className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
              <div><label className="label-caps block mb-1">Start Date</label>
                <Input data-testid="leave-start" required type="date" value={leave.start_date} onChange={(e) => setLeave({ ...leave, start_date: e.target.value })} className="rounded-lg border-black/15 h-11" /></div>
              <div><label className="label-caps block mb-1">End Date</label>
                <Input data-testid="leave-end" required type="date" value={leave.end_date} onChange={(e) => setLeave({ ...leave, end_date: e.target.value })} className="rounded-lg border-black/15 h-11" /></div>
              <div className="md:col-span-2"><label className="label-caps block mb-1">Reason</label>
                <Input data-testid="leave-reason" required value={leave.reason} onChange={(e) => setLeave({ ...leave, reason: e.target.value })} className="rounded-lg border-black/15 h-11" placeholder="e.g. Family function, medical, vacation" /></div>
              <div className="md:col-span-4 flex justify-end">
                <button data-testid="submit-leave" type="submit" disabled={leaveBusy} className="btn-primary disabled:opacity-50">{leaveBusy ? "Submitting…" : "Submit request"}</button>
              </div>
            </form>
            <div className="mt-6">
              <div className="label-caps mb-2">Your leave history</div>
              <div className="border border-black/10 rounded-xl overflow-hidden">
                <table className="fms-table w-full" data-testid="leave-history">
                  <thead><tr><th>Dates</th><th>Reason</th><th>Status</th><th>Decision</th></tr></thead>
                  <tbody>
                    {leaves.map((l) => (
                      <tr key={l.id} data-testid={`leave-row-${l.id}`}>
                        <td className="font-mono-tight text-xs">{l.start_date} → {l.end_date}</td>
                        <td className="text-sm">{l.reason}</td>
                        <td><span className={`px-2 py-0.5 text-xs font-mono-tight uppercase rounded ${statusTone(l.status)}`}>{l.status}</span></td>
                        <td className="text-xs text-neutral-500 font-mono-tight">{l.decided_by_name ? `${l.decided_by_name}` : "—"}</td>
                      </tr>
                    ))}
                    {leaves.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-neutral-500">No leave requests yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={savePassword} className="border border-neutral-200 bg-white p-6 space-y-3 rounded-2xl" data-testid="password-form">
          <div className="label-caps">Change Password</div>
          <div><label className="label-caps block mb-1">Current Password</label><Input type="password" required value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="rounded-lg border-black/15" /></div>
          <div><label className="label-caps block mb-1">New Password</label><Input type="password" required minLength={6} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="rounded-lg border-black/15" /></div>
          <button className="btn-primary">Update Password</button>
        </form>
      </div>
    </div>
  );
}
